import {
    Box,
    Flex,
    IconButton,
    Image,
    keyframes,
} from "@chakra-ui/react";
import {ArrowBackIcon, ArrowForwardIcon} from "@chakra-ui/icons";
import React, {useEffect, useState} from "react";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import scheduledBellImage from "assets/img/dashboard-slider/scheduled-bell.png";
import bellDashboardImage from "assets/img/dashboard-slider/bell-dashboard.png";
import speakerStatusVolumeImage from "assets/img/dashboard-slider/speaker-status-volume.png";
import bellStatusImage from "assets/img/dashboard-slider/bell_status.png";

const slides = [
    {
        image: bellStatusImage,
        alt: "Students walking through a school hallway with a scheduled digital bell",
    },
    {
        image: scheduledBellImage,
        alt: "Students walking through a school hallway with a scheduled digital bell",
    },
    {
        image: bellDashboardImage,
        alt: "School administrator managing the digital bell dashboard",
    },
    {
        image: speakerStatusVolumeImage,
        alt: "Online school speaker showing successful bell playback and volume level",
    },
];

const SLIDE_DURATION = 6000;

// Quick crossfade + a slow "Ken Burns" zoom while each slide is shown.
const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;
const kenBurns = keyframes`
    from { transform: scale(1); }
    to   { transform: scale(1.08); }
`;
// Autoplay progress indicator.
const grow = keyframes`
    from { width: 0%; }
    to   { width: 100%; }
`;

const DashboardMessageSlider = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const activeSlide = slides[activeIndex];

    useEffect(() => {
        if (isPaused) {
            return undefined;
        }

        const interval = setInterval(() => {
            setActiveIndex((currentIndex) => (currentIndex + 1) % slides.length);
        }, SLIDE_DURATION);

        return () => clearInterval(interval);
    }, [isPaused]);

    const showPrevious = () => {
        setActiveIndex((currentIndex) => (currentIndex - 1 + slides.length) % slides.length);
    };

    const showNext = () => {
        setActiveIndex((currentIndex) => (currentIndex + 1) % slides.length);
    };

    const sliderHeight = {base: "140px", sm: "160px", md: "180px", lg: "200px"};

    return (
        <Card
            role="group"
            h={sliderHeight}
            minHeight={sliderHeight}
            maxH={sliderHeight}
            alignSelf="start"
            p="0"
            overflow="hidden"
            borderRadius="15px"
            transition="box-shadow 0.25s ease"
            _hover={{boxShadow: "0 12px 30px rgba(0, 0, 0, 0.18)"}}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
        >
            <CardBody w="100%" h="100%" minH={sliderHeight} p="0" position="relative" overflow="hidden">
                <Image
                    key={activeSlide.image}
                    src={activeSlide.image}
                    alt={activeSlide.alt}
                    position="absolute"
                    inset="0"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    objectPosition="center"
                    animation={`${fadeIn} 0.6s ease-out, ${kenBurns} ${SLIDE_DURATION}ms ease-out forwards`}
                    style={{animationPlayState: isPaused ? "paused" : "running"}}
                />

                {/* Soft gradient so controls stay readable on bright images */}
                <Box
                    position="absolute"
                    inset="0"
                    bgGradient="linear(to-t, blackAlpha.500, transparent 45%)"
                    pointerEvents="none"
                />

                <Flex
                    position="absolute"
                    inset="0"
                    align="center"
                    justify="space-between"
                    px={{base: "6px", md: "10px"}}
                    pointerEvents="none"
                    opacity={{base: 1, md: 0}}
                    transform={{base: "none", md: "scale(0.92)"}}
                    transition="opacity 0.25s ease, transform 0.25s ease"
                    _groupHover={{opacity: 1, transform: "scale(1)"}}
                >
                    <IconButton
                        aria-label="Previous image"
                        icon={<ArrowBackIcon/>}
                        size={{base: "sm", md: "md"}}
                        isRound
                        color="white"
                        bg="blackAlpha.500"
                        backdropFilter="blur(4px)"
                        _hover={{bg: "blackAlpha.700", transform: "scale(1.1)"}}
                        _active={{transform: "scale(0.95)"}}
                        transition="all 0.15s ease"
                        onClick={showPrevious}
                        pointerEvents="auto"
                    />
                    <IconButton
                        aria-label="Next image"
                        icon={<ArrowForwardIcon/>}
                        size={{base: "sm", md: "md"}}
                        isRound
                        color="white"
                        bg="blackAlpha.500"
                        backdropFilter="blur(4px)"
                        _hover={{bg: "blackAlpha.700", transform: "scale(1.1)"}}
                        _active={{transform: "scale(0.95)"}}
                        transition="all 0.15s ease"
                        onClick={showNext}
                        pointerEvents="auto"
                    />
                </Flex>

                <Flex
                    position="absolute"
                    bottom="12px"
                    left="0"
                    right="0"
                    justify="center"
                    aria-label="Choose dashboard image"
                >
                    {slides.map((slide, index) => (
                        <Box
                            as="button"
                            type="button"
                            key={slide.image}
                            aria-label={`Show image ${index + 1}`}
                            aria-pressed={index === activeIndex}
                            w={index === activeIndex ? "22px" : "8px"}
                            h="8px"
                            mx="3px"
                            borderRadius="full"
                            bg={index === activeIndex ? "white" : "whiteAlpha.600"}
                            boxShadow="0 1px 4px rgba(0, 0, 0, 0.35)"
                            transition="all 0.25s ease"
                            _hover={{bg: "white"}}
                            onClick={() => setActiveIndex(index)}
                        />
                    ))}
                </Flex>

                {/* Autoplay progress bar */}
                <Box position="absolute" bottom="0" left="0" right="0" h="3px" bg="whiteAlpha.300">
                    <Box
                        key={activeIndex}
                        h="100%"
                        bg="white"
                        animation={`${grow} ${SLIDE_DURATION}ms linear`}
                        style={{animationPlayState: isPaused ? "paused" : "running"}}
                    />
                </Box>
            </CardBody>
        </Card>
    );
};

export default DashboardMessageSlider;
